import * as ExcelJS from "exceljs";
import {
  Chart,
  ArcElement,
  BarElement,
  BarController,
  CategoryScale,
  DoughnutController,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { addJustupBranding, setJustupMetadata } from "../../../../shared/utils/excel-branding.util";
import { NC_STATS_DATA, NcAxisBucket } from "../../models/data.model";
import {
  QUALITE_C,
  QUALITE_CHART_COLORS,
  colLetter,
  downloadXlsx,
  qualiteHeaderStyle,
  qualiteRowStyle,
  qualiteSectionTitle,
  qualiteStatLabelStyle,
  qualiteStatStyle,
  qualiteSubHeaderStyle,
  renderChartToPng,
  todayIso,
} from "../qualite-excel-shared.utils";

// Enregistrement Chart.js pour rendu offscreen.
Chart.register(
  ArcElement,
  BarElement,
  BarController,
  CategoryScale,
  DoughnutController,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
);

// ─── Helpers ─────────────────────────────────────────────────────────────────

function autoFilter(ws: ExcelJS.Worksheet, headerRow: number, lastCol: number): void {
  ws.autoFilter = {
    from: { row: headerRow, column: 1 },
    to: { row: headerRow, column: lastCol },
  };
}

/** Donut origine en image PNG (5 segments interne/externe/mixte/nonRenseigne). */
function buildOrigineDonutPng(stats: NC_STATS_DATA["stats"]): string | null {
  const r = stats?.origineRepartition;
  if (!r) return null;
  const segs = [
    { label: "Interne", value: r.interne, color: QUALITE_C.origineInterne },
    { label: "Externe", value: r.externe, color: QUALITE_C.origineExterne },
    { label: "Mixte", value: r.mixte, color: QUALITE_C.origineMixte },
    { label: "Non renseigné", value: r.nonRenseigne, color: QUALITE_C.origineNonRenseigne },
  ].filter((s) => s.value > 0);
  if (!segs.length) return null;

  return renderChartToPng(
    (ctx) =>
      new Chart(ctx, {
        type: "doughnut",
        data: {
          labels: segs.map((s) => s.label),
          datasets: [
            {
              data: segs.map((s) => s.value),
              backgroundColor: segs.map((s) => `#${s.color.slice(2)}`),
              borderColor: "#fff",
              borderWidth: 2,
            },
          ],
        },
        options: {
          responsive: false,
          animation: false,
          plugins: {
            legend: { display: true, position: "bottom", labels: { font: { size: 11 } } },
            title: { display: true, text: "Origine des NC", font: { size: 13, weight: "bold" } },
            tooltip: { enabled: false },
          },
        },
      }),
    520,
    400,
  );
}

function buildBarChartPng(title: string, buckets: NcAxisBucket[]): string | null {
  if (!buckets?.length) return null;
  const top = buckets.slice(0, 10);
  return renderChartToPng(
    (ctx) =>
      new Chart(ctx, {
        type: "bar",
        data: {
          labels: top.map((b) => b.label),
          datasets: [
            {
              label: "Nombre",
              data: top.map((b) => b.count),
              backgroundColor: QUALITE_CHART_COLORS.slice(0, top.length),
              borderRadius: 4,
            },
          ],
        },
        options: {
          responsive: false,
          animation: false,
          indexAxis: "y",
          plugins: {
            legend: { display: false },
            title: { display: true, text: title, font: { size: 13, weight: "bold" } },
          },
          scales: {
            x: { beginAtZero: true, ticks: { precision: 0, font: { size: 10 } } },
            y: { ticks: { font: { size: 10 } } },
          },
        },
      }),
    600,
    Math.max(280, 40 + top.length * 28),
  );
}

function buildMonthLinePng(rows: NC_STATS_DATA["analytics"]["byMonth"]): string | null {
  if (!rows?.length) return null;
  return renderChartToPng(
    (ctx) =>
      new Chart(ctx, {
        type: "line",
        data: {
          labels: rows.map((r) => r.month),
          datasets: [
            {
              label: "NC créées",
              data: rows.map((r) => r.count),
              borderColor: "#36A2EB",
              backgroundColor: "rgba(54,162,235,0.18)",
              tension: 0.3,
              fill: true,
              pointRadius: 3,
            },
          ],
        },
        options: {
          responsive: false,
          animation: false,
          plugins: {
            legend: { display: false },
            title: {
              display: true,
              text: "Évolution mensuelle des NC",
              font: { size: 13, weight: "bold" },
            },
          },
          scales: {
            x: { ticks: { font: { size: 10 } } },
            y: { beginAtZero: true, ticks: { precision: 0, font: { size: 10 } } },
          },
        },
      }),
    700,
    320,
  );
}

// ─── Sheets ──────────────────────────────────────────────────────────────────

function writeCoverSheet(wb: ExcelJS.Workbook, data: NC_STATS_DATA): void {
  const ws = wb.addWorksheet("Synthèse", {
    views: [{ state: "frozen", ySplit: 1 }],
    properties: { tabColor: { argb: QUALITE_C.headerBg } },
  });
  [22, 18, 18, 18, 18, 18].forEach((w, i) => (ws.getColumn(i + 1).width = w));

  // Titre
  ws.mergeCells("A1:F1");
  const title = ws.getCell("A1");
  qualiteSectionTitle(title, "Non-conformités — Synthèse 12 derniers mois");
  ws.getRow(1).height = 32;

  // Sous-titre période
  ws.mergeCells("A2:F2");
  const sub = ws.getCell("A2");
  sub.value = `Document généré le ${new Date().toLocaleDateString("fr-FR")} par JustUp — IFS / ISO 9001`;
  sub.font = { size: 10, italic: true, color: { argb: "FF667085" } };

  // KPI cards (5 cards sur ligne 4-6)
  const cards: { label: string; value: string | number; bg: string }[] = [
    { label: "Total NC", value: data.analytics.totalCount, bg: QUALITE_C.statBlue },
    { label: "Ouvertes", value: data.stats.ouvertes, bg: QUALITE_C.statPurple },
    {
      label: "Critiques ouvertes",
      value: data.stats.critiquesOuvertes,
      bg: QUALITE_C.statRed,
    },
    {
      label: "Coût total estimé (€)",
      value: data.analytics.coutTotal,
      bg: QUALITE_C.statGreen,
    },
    {
      label: "Taux d'échappées (%)",
      value: data.stats.tauxEchappees == null ? "—" : data.stats.tauxEchappees,
      bg: QUALITE_C.statOrange,
    },
  ];
  cards.forEach((c, i) => {
    const labelCell = ws.getCell(4, i + 1);
    labelCell.value = c.label;
    qualiteStatLabelStyle(labelCell, c.bg);
    const valCell = ws.getCell(5, i + 1);
    valCell.value = c.value;
    qualiteStatStyle(valCell, c.bg);
    if (typeof c.value === "number") valCell.numFmt = "#,##0.##";
  });
  ws.getRow(4).height = 18;
  ws.getRow(5).height = 28;

  // Délais détection / enregistrement (KPI complémentaires ISO 9001)
  ws.mergeCells("A8:F8");
  qualiteSectionTitle(ws.getCell("A8"), "Délais qualité (ISO 9001)");
  ws.getRow(8).height = 22;

  const delaiRows: { label: string; value: string | number }[] = [
    {
      label: "Délai de détection moyen (h) — constatation - occurrence",
      value:
        data.analytics.delaiDetectionMoyenHeures == null
          ? "—"
          : data.analytics.delaiDetectionMoyenHeures,
    },
    {
      label: "→ NC dans le calcul",
      value: data.analytics.delaiDetectionSamples,
    },
    {
      label: "Délai d'enregistrement moyen (h) — ouverture - constatation",
      value:
        data.analytics.delaiEnregistrementMoyenHeures == null
          ? "—"
          : data.analytics.delaiEnregistrementMoyenHeures,
    },
    {
      label: "→ NC dans le calcul",
      value: data.analytics.delaiEnregistrementSamples,
    },
  ];
  let r = 10;
  for (const row of delaiRows) {
    ws.mergeCells(r, 1, r, 5);
    const a = ws.getCell(r, 1);
    a.value = row.label;
    qualiteRowStyle(a, r % 2 === 0);
    a.font = { ...a.font, bold: !row.label.startsWith("→") };
    a.alignment = { vertical: "middle", indent: row.label.startsWith("→") ? 2 : 0 };
    const b = ws.getCell(r, 6);
    b.value = row.value;
    qualiteRowStyle(b, r % 2 === 0);
    b.alignment = { horizontal: "right" };
    if (typeof row.value === "number") b.numFmt = "#,##0.##";
    r += 1;
  }

  // Donut origine
  const donut = buildOrigineDonutPng(data.stats);
  if (donut) {
    const imgId = wb.addImage({ base64: donut, extension: "png" });
    ws.addImage(imgId, {
      tl: { col: 0, row: r + 1 } as any,
      ext: { width: 520, height: 400 },
    });
  }
}

function writeAxisSheet(
  wb: ExcelJS.Workbook,
  sheetName: string,
  buckets: NcAxisBucket[],
  chartTitle?: string,
): void {
  const ws = wb.addWorksheet(sheetName, {
    views: [{ state: "frozen", ySplit: 5 }],
  });
  [32, 16, 18].forEach((w, i) => (ws.getColumn(i + 1).width = w));

  ws.mergeCells("A1:C1");
  qualiteSectionTitle(ws.getCell("A1"), sheetName);
  ws.getRow(1).height = 28;

  const headerRow = 5;
  ["Libellé", "Nombre", "Coût (€)"].forEach((h, i) => {
    const c = ws.getCell(headerRow, i + 1);
    c.value = h;
    qualiteHeaderStyle(c);
  });
  ws.getRow(headerRow).height = 22;
  autoFilter(ws, headerRow, 3);

  let r = headerRow + 1;
  for (const b of buckets) {
    const cells: (string | number)[] = [b.label, b.count, b.cout];
    cells.forEach((v, i) => {
      const c = ws.getCell(r, i + 1);
      c.value = v;
      qualiteRowStyle(c, r % 2 === 0);
      if (i > 0) {
        c.alignment = { horizontal: "right" };
        c.numFmt = i === 2 ? "#,##0.00" : "#,##0";
      } else {
        c.font = { ...c.font, bold: true };
      }
    });
    r += 1;
  }

  // Total formula
  if (buckets.length > 0) {
    const tr = r + 1;
    const labelCell = ws.getCell(tr, 1);
    labelCell.value = "TOTAL";
    qualiteSubHeaderStyle(labelCell);
    labelCell.alignment = { horizontal: "right" };
    const sumCount = ws.getCell(tr, 2);
    sumCount.value = {
      formula: `SUM(B${headerRow + 1}:B${r - 1})`,
    } as any;
    qualiteSubHeaderStyle(sumCount);
    sumCount.alignment = { horizontal: "right" };
    sumCount.numFmt = "#,##0";
    const sumCout = ws.getCell(tr, 3);
    sumCout.value = {
      formula: `SUM(C${headerRow + 1}:C${r - 1})`,
    } as any;
    qualiteSubHeaderStyle(sumCout);
    sumCout.alignment = { horizontal: "right" };
    sumCout.numFmt = "#,##0.00";
  }

  // Bar chart en image (top 10)
  if (chartTitle) {
    const png = buildBarChartPng(chartTitle, buckets);
    if (png) {
      const imgId = wb.addImage({ base64: png, extension: "png" });
      const startRow = Math.max(r + 3, 8);
      ws.addImage(imgId, {
        tl: { col: 4, row: startRow } as any,
        ext: { width: 600, height: Math.max(280, 40 + Math.min(buckets.length, 10) * 28) },
      });
    }
  }
}

function writeMonthSheet(
  wb: ExcelJS.Workbook,
  rows: NC_STATS_DATA["analytics"]["byMonth"],
): void {
  const ws = wb.addWorksheet("Évolution mensuelle", {
    views: [{ state: "frozen", ySplit: 5 }],
  });
  [16, 16, 18].forEach((w, i) => (ws.getColumn(i + 1).width = w));

  ws.mergeCells("A1:C1");
  qualiteSectionTitle(ws.getCell("A1"), "Évolution mensuelle des NC");
  ws.getRow(1).height = 28;

  const headerRow = 5;
  ["Mois", "Nombre NC", "Coût (€)"].forEach((h, i) => {
    const c = ws.getCell(headerRow, i + 1);
    c.value = h;
    qualiteHeaderStyle(c);
  });
  ws.getRow(headerRow).height = 22;
  autoFilter(ws, headerRow, 3);

  let r = headerRow + 1;
  for (const m of rows) {
    const a = ws.getCell(r, 1);
    a.value = m.month;
    qualiteRowStyle(a, r % 2 === 0);
    a.font = { ...a.font, bold: true };
    const b = ws.getCell(r, 2);
    b.value = m.count;
    qualiteRowStyle(b, r % 2 === 0);
    b.alignment = { horizontal: "right" };
    b.numFmt = "#,##0";
    const c = ws.getCell(r, 3);
    c.value = m.cout;
    qualiteRowStyle(c, r % 2 === 0);
    c.alignment = { horizontal: "right" };
    c.numFmt = "#,##0.00";
    r += 1;
  }

  if (rows.length > 0) {
    const tr = r + 1;
    const a = ws.getCell(tr, 1);
    a.value = "TOTAL";
    qualiteSubHeaderStyle(a);
    a.alignment = { horizontal: "right" };
    const b = ws.getCell(tr, 2);
    b.value = { formula: `SUM(B${headerRow + 1}:B${r - 1})` } as any;
    qualiteSubHeaderStyle(b);
    b.alignment = { horizontal: "right" };
    b.numFmt = "#,##0";
    const c = ws.getCell(tr, 3);
    c.value = { formula: `SUM(C${headerRow + 1}:C${r - 1})` } as any;
    qualiteSubHeaderStyle(c);
    c.alignment = { horizontal: "right" };
    c.numFmt = "#,##0.00";
  }

  // Line chart évolution
  const png = buildMonthLinePng(rows);
  if (png) {
    const imgId = wb.addImage({ base64: png, extension: "png" });
    ws.addImage(imgId, {
      tl: { col: 4, row: 4 } as any,
      ext: { width: 700, height: 320 },
    });
  }
}

function writeCriticalSheet(
  wb: ExcelJS.Workbook,
  rows: NC_STATS_DATA["analytics"]["criticalOpen"],
): void {
  const ws = wb.addWorksheet("NC critiques ouvertes", {
    views: [{ state: "frozen", ySplit: 5 }],
    properties: { tabColor: { argb: QUALITE_C.severityCritique } },
  });
  [14, 16, 50, 22, 16, 16].forEach((w, i) => (ws.getColumn(i + 1).width = w));

  ws.mergeCells("A1:F1");
  qualiteSectionTitle(ws.getCell("A1"), "NC critiques actuellement ouvertes");
  ws.getRow(1).height = 28;

  const headerRow = 5;
  ["N°", "Type", "Description", "Tiers", "Date ouverture", "Coût estimé (€)"].forEach(
    (h, i) => {
      const c = ws.getCell(headerRow, i + 1);
      c.value = h;
      qualiteHeaderStyle(c);
    },
  );
  ws.getRow(headerRow).height = 22;
  autoFilter(ws, headerRow, 6);

  let r = headerRow + 1;
  for (const nc of rows) {
    const cells: (string | number | Date | null)[] = [
      nc.numero,
      nc.type,
      nc.description ?? "",
      nc.tiersLabel ?? "",
      nc.dateOuverture ? new Date(nc.dateOuverture as string) : null,
      nc.coutEstime ?? null,
    ];
    cells.forEach((v, i) => {
      const c = ws.getCell(r, i + 1);
      c.value = v as any;
      qualiteRowStyle(c, r % 2 === 0);
      if (i === 0) {
        c.font = { ...c.font, bold: true, color: { argb: QUALITE_C.severityCritique } };
      }
      if (i === 4 && v) c.numFmt = "dd/mm/yyyy";
      if (i === 5) {
        c.alignment = { horizontal: "right" };
        c.numFmt = "#,##0.00";
      }
    });
    r += 1;
  }
}

// ─── Public ──────────────────────────────────────────────────────────────────

export async function exportNcStatsExcel(data: NC_STATS_DATA): Promise<void> {
  const wb = new ExcelJS.Workbook();
  setJustupMetadata(wb, {
    title: "Non-conformités — Synthèse",
    subject: "Tableau de bord qualité IFS / ISO 9001",
    description:
      "Synthèse des non-conformités sur 12 mois glissants : KPI ouvertes / critiques / coût, " +
      "ventilation par type / gravité / nature, top fournisseurs et articles, évolution mensuelle, " +
      "NC critiques ouvertes. Document généré par JustUp.",
    keywords: "JustUp, IFS, ISO 9001, qualité, non-conformité, NC, dashboard",
  });

  // 1. Synthèse / KPI / donut origine
  writeCoverSheet(wb, data);

  // 2. Ventilations multi-axes
  writeAxisSheet(wb, "Par type", data.analytics.byType, "NC par type");
  writeAxisSheet(wb, "Par gravité", data.analytics.byGravite, "NC par gravité");
  writeAxisSheet(wb, "Par état", data.analytics.byEtat, "NC par état");
  if (data.analytics.byNature?.length) {
    writeAxisSheet(wb, "Par nature", data.analytics.byNature, "NC par nature");
  }
  if (data.analytics.topTiers?.length) {
    writeAxisSheet(wb, "Top tiers", data.analytics.topTiers, "Top 10 tiers (NC)");
  }
  if (data.analytics.topArticles?.length) {
    writeAxisSheet(wb, "Top articles", data.analytics.topArticles, "Top 10 articles (NC)");
  }

  // 3. Évolution mensuelle (line chart)
  writeMonthSheet(wb, data.analytics.byMonth);

  // 4. NC critiques ouvertes
  if (data.analytics.criticalOpen?.length) {
    writeCriticalSheet(wb, data.analytics.criticalOpen);
  }

  // Branding sur toutes les feuilles (logo + lien justup.io en haut à droite)
  for (const ws of wb.worksheets) {
    const lastCol = Math.max(6, ws.actualColumnCount || 6);
    await addJustupBranding(wb, ws, lastCol);
  }

  const buffer = (await wb.xlsx.writeBuffer()) as ArrayBuffer;
  downloadXlsx(buffer, `nc-stats-${todayIso()}.xlsx`);
}
